import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { FilterTable } from 'gnos-tables'
import cx from 'classnames'
import _ from 'lodash'

import NoData from '../../../../../../../../../components/Workspace/components/NoData'
import Entity from '../../../../../../../../../../../components/TableCell/components/Entity'
import Signals from '../../../../../../../../../../../components/TableCell/components/Signals'

import { LOCALE_PREFIX } from '../../constants'

import hocConnect from './hocConnect'
import './style.scss'
import Money from '../../../../../../../../../../../components/TableCell/components/Money'
import CustomDate from '../../../../../../../../../../../components/CustomDate'
import Link from '../../../../../../../../../../../components/TableCell/components/Link'
import FailedLoad from '../../../../../../../../../components/Workspace/components/FailedLoad'

@injectIntl
@hocConnect
export default class PaymentOrders extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        companyAsCreditorLoadTableData: PropTypes.func.isRequired,
        companyAsCreditorTableChange: PropTypes.func.isRequired,
        customData: PropTypes.object,
        data: PropTypes.array,
        dataLocale: PropTypes.string,
        failed: PropTypes.bool,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object,
        isLoading: PropTypes.bool,
        level: PropTypes.string.isRequired,
        loaded: PropTypes.bool,
        locale: PropTypes.string.isRequired,
        locked: PropTypes.bool,
        module: PropTypes.string.isRequired,
        pagination: PropTypes.object,
        sorted: PropTypes.array,
        tableId: PropTypes.string.isRequired,
        tableSelector: PropTypes.string.isRequired,
    }

    componentDidMount() {
        const { loaded, dataLocale, locale } = this.props

        if (!loaded || dataLocale !== locale) this.loadData()
    }

    shouldComponentUpdate(nextProps) {
        const {
            customData,
            data,
            isLoading,
            level,
            locale,
            pagination,
            sorted,
            tableId,
            failed,
            loaded,
            tableSelector,
        } = this.props

        if (!_.isEqual(customData, nextProps.customData)) return true
        if (!_.isEqual(data, nextProps.data)) return true
        if (!_.isEqual(isLoading, nextProps.isLoading)) return true
        if (!_.isEqual(level, nextProps.level)) return true
        if (!_.isEqual(locale, nextProps.locale)) return true
        if (!_.isEqual(pagination, nextProps.pagination)) return true
        if (!_.isEqual(sorted, nextProps.sorted)) return true
        if (!_.isEqual(failed, nextProps.failed)) return true
        if (!_.isEqual(loaded, nextProps.loaded)) return true
        if (!_.isEqual(tableId, nextProps.tableId)) return true
        if (!_.isEqual(tableSelector, nextProps.tableSelector)) return true

        return false
    }

    componentDidUpdate(prevProps) {
        const { level, locale, dataLocale } = this.props

        if (prevProps.level !== level) this.loadData()
        if (locale !== dataLocale && dataLocale) this.loadData()
    }

    loadData = () => {
        const { tableId, tableSelector, id, module, block, level, customData, companyAsCreditorLoadTableData } =
            this.props

        companyAsCreditorLoadTableData(tableId, tableSelector, id, module, block, level, customData)
    }

    handleTableChange = (changeData) => {
        const { tableId, tableSelector, id, module, block, level, customData, companyAsCreditorTableChange } =
            this.props

        companyAsCreditorTableChange(tableId, tableSelector, id, module, block, level, customData, changeData)
    }

    render() {
        const { tableId, intl, data, isLoading, pagination, sorted, failed, locked } = this.props

        if (locked && !isLoading && !failed) {
            return (
                <div className={'cac-payment-orders'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.lockedTab` })} />
                </div>
            )
        }

        if (failed && !isLoading) {
            return (
                <div className={'cac-payment-orders'}>
                    <FailedLoad
                        customCallback={this.loadData}
                        customText={intl.formatMessage({ id: 'global.failedToLoadTabContent' })}
                        show
                    />
                </div>
            )
        }

        if (_.isEmpty(data) && !isLoading) {
            return (
                <div className={'cac-payment-orders'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.noPaymentOrdersData` })} />
                </div>
            )
        }

        return (
            <div className={'cac-payment-orders'}>
                <FilterTable
                    id={tableId}
                    data={data ?? []}
                    loading={isLoading}
                    columnsDefinition={[
                        {
                            accessor: 'mandatorySubject',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.mandatorySubject` }),
                            Cell: (value) => {
                                if (!_.isNil(_.get(value, 'value.type'))) {
                                    return (
                                        <div className={'centered-cell'}>
                                            <Entity
                                                entity={_.get(value, 'value', null)}
                                                type={_.get(value, 'value.type', null)}
                                                inactive={_.get(value, 'value.inactive', false)}
                                                value={_.get(value, 'value.name', null)}
                                                entId={_.get(value, 'value.id', null)}
                                                rowInactive={_.get(value, 'original.inactive')}
                                            />
                                        </div>
                                    )
                                }

                                return (
                                    <div
                                        className={cx(
                                            'centered-cell',
                                            _.get(value, 'original.inactive', false) && 'inactive'
                                        )}
                                    >
                                        <FormattedMessage id={`${LOCALE_PREFIX}.anonymousPerson`} />
                                    </div>
                                )
                            },
                        },
                        {
                            accessor: 'mandatorySubject.indicators',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.signals` }),
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    <Signals
                                        id={_.get(value, 'original.mandatorySubject.id', null)}
                                        risk={_.get(value, 'value.risk', 0)}
                                        caution={_.get(value, 'value.caution', 0)}
                                        notice={_.get(value, 'value.notice', 0)}
                                    />
                                </div>
                            ),
                            width: 150,
                        },
                        {
                            accessor: 'amount',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.amount` }),
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    <Money value={_.get(value, 'value', {})} showCurrency />
                                </div>
                            ),
                            width: 150,
                            sortable: true,
                        },
                        {
                            accessor: 'folderId',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.folderId` }),
                            width: 160,
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    <Link
                                        label={_.get(value, 'value', '')}
                                        url={_.get(value, 'original.eventUrl', '')}
                                        showIcon
                                        iconType={'pdf'}
                                        openOnNewTab
                                    />
                                </div>
                            ),
                        },
                        {
                            accessor: 'decisionDate',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.decisionDate` }),
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    <CustomDate
                                        precision={_.get(value, 'value.precision', null)}
                                        date={_.get(value, 'value.value', null)}
                                    />
                                </div>
                            ),
                            width: 125,
                            sortable: true,
                        },
                    ]}
                    settings={{
                        pagination,
                        sorted,
                    }}
                    onTableChange={this.handleTableChange}
                    texts={{
                        pagination: {
                            page: intl.formatMessage({ id: 'global.page' }),
                            from: intl.formatMessage({ id: 'global.of' }),
                            rowCount: intl.formatMessage({ id: 'global.rowCount' }),
                            rowShow: intl.formatMessage({ id: 'global.rowShow' }),
                            all: intl.formatMessage({ id: 'global.all' }),
                        },
                    }}
                />
            </div>
        )
    }
}
